import React from 'react';
import {Picture} from '../Picture.jsx';
import classNames from 'classnames';
import {TextPosition} from '../text-modifiers';
import {Headline} from '../Headline.jsx';
import {Subline} from '../Subline.jsx';
import {CallToAction} from '../CallToAction.jsx';
import {Link} from '../Link.jsx';
import {TrackingContextAppend} from '../TrackingContext.jsx';
import {CfEntry} from '../CfPreviewContext.jsx';
import {Video} from '../Video.jsx';
import {PriceTag} from '../PriceTag.jsx';
import {LinkBarV2} from './LinkBarV2.jsx';
import {XRay} from '../XRay.jsx';

/**
 * @typedef {TeaserLinkModel} FocusTeaserModel
 * @property {string} entryId
 * @property {'FOCUS_TEASER'} type
 * @property {string} headline
 * @property {string} subline
 * @property {string} backgroundColor
 * @property {ImageModel | null} backgroundImage
 * @property {VideoModel | null} video
 * @property {string} callToAction
 * @property {string} contentType
 * @property {TextColor} textColor
 * @property {TextPosition} textPosition
 * @property {string[]} devices
 * @property {PriceTagModel[]} priceTags
 * @property {LinkBarV2} linkBar
 */

/**
 * @param {import('./ArticleOverviewTeaserContent.jsx').Device} Device
 */

/**
 * @param {FocusTeaserModel} model
 * @param {Device} device
 * @returns {JSX.Element}
 */
export function FocusTeaser({model, device, style}) {
    const isMobile =
        device.name === 'mobile' || device.name === 'app' || device.name === 'unknown' || device.name === undefined;

    return (
        <TrackingContextAppend value={{id: model.entryId}}>
            <CfEntry entryId={model.entryId} contentType={model.contentType}>
                <article
                    className={classNames(
                        'content-platform__teaser',
                        'content-platform__teaser--focus',
                        model.devices.map((device) => `content-platform__teaser--device-${device.toLowerCase()}`),
                        `content-platform__teaser--current-device-${device.name}`,
                    )}
                    style={style}
                >
                    <XRay fieldId="internalName"/>
                    <div
                        className="content-platform__teaser__container"
                        style={{backgroundColor: model.backgroundColor}}
                    >
                        <Link model={model} className={'content-platform__teaser__link'}/>
                        <div className="content-platform__teaser__image_price_tags-container">
                            <Video model={model} isMobile={isMobile} className={'-focus'}>
                                {model.backgroundImage ? (
                                    <div className="content-platform__teaser__img-container">
                                        <Picture model={model.backgroundImage} xxlarge={1920} xlarge={1568}/>
                                    </div>
                                ) : (
                                    <div className="content-platform__teaser__spacer"/>
                                )}
                            </Video>
                            {model.priceTags?.map((priceTag) => (
                                <PriceTag key={priceTag.entryId} model={priceTag}/>
                            ))}
                        </div>

                        <div
                            className={classNames(
                                'content-platform__teaser__content',
                                model.subline && '-with-subline',
                            )}
                        >
                            <div
                                className={classNames(
                                    'content-platform__teaser__main',
                                    model.subline && '-with-subline',
                                    TextPosition[model.textPosition],
                                )}
                            >
                                <Headline text={model.headline} textColor={model.textColor}/>

                                <Subline text={model.subline} textColor={model.textColor}/>

                                <CallToAction text={model.callToAction}/>
                            </div>
                        </div>
                    </div>
                    {model.linkBar && <LinkBarV2 model={model.linkBar}/>}
                </article>
            </CfEntry>
        </TrackingContextAppend>
    );
}
